<template>
  <div class="map-bulk-page py-3">
    <header class="map-toolbar">
      <div class="map-toolbar__title">
        <span class="map-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>MAP Bulk Orders Mark</h1>
        </div>
      </div>
      <div class="map-toolbar__actions">
        <span v-if="lastUpdatedLabel && !loading" class="map-updated">Updated {{ lastUpdatedLabel }}</span>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="map-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="map-alert">{{ successMessage }}</CAlert>

    <section class="map-filter-card">
      <div class="map-filter-bar">
        <DateRangePicker
          v-model="dateRange"
          :disabled="loading"
          @preset="onSearch"
        />
        <div class="map-filter-actions">
          <CButton
            color="primary"
            class="map-action-btn"
            :disabled="loading"
            @click="onSearch"
          >
            {{ loading ? 'Loading…' : 'Search' }}
          </CButton>
        </div>
      </div>
      <div v-if="appliedRangeLabel" class="map-filter-footnote">
        Showing {{ appliedRangeLabel }}
      </div>
    </section>

    <div class="report-tabs" role="tablist">
      <button
        v-for="tab in tabsWithCounts"
        :key="tab.key"
        type="button"
        role="tab"
        class="report-tab"
        :class="{ 'is-active': activeTab === tab.key }"
        :aria-selected="activeTab === tab.key"
        @click="activeTab = tab.key"
      >
        <span class="tab-label">{{ tab.label }}</span>
        <span v-if="!loading && tab.count != null" class="tab-count">{{ tab.count }}</span>
      </button>
    </div>

    <UnmarkedOrdersTab
      v-if="activeTab === 'unmarked'"
      :orders="unmarkedOrders.data"
      :pagination="unmarkedPagination"
      :loading="loading"
      @select="openMarkModal"
      @page-change="onUnmarkedPageChange"
    />

    <MarkedOrdersTab
      v-else-if="activeTab === 'marked'"
      :orders="markedOrders.data"
      :pagination="markedPagination"
      :loading="loading"
      @select="openChangeModal"
      @page-change="onMarkedPageChange"
    />

    <ReportTab
      v-else-if="activeTab === 'report'"
      :report="report"
      :loading="loading"
    />

    <MarkOrderModal
      :show="modal.show"
      :order="modal.order"
      :users="users"
      :mode="modal.mode"
      :sl-no="modal.slNo"
      :submitting="modal.submitting"
      @close="closeModal"
      @submit="submitMark"
    />
  </div>
</template>

<script>
import axios from 'axios'
import UnmarkedOrdersTab from './MvpBulkOrders/UnmarkedOrdersTab.vue'
import MarkedOrdersTab from './MvpBulkOrders/MarkedOrdersTab.vue'
import ReportTab from './MvpBulkOrders/ReportTab.vue'
import MarkOrderModal from './MvpBulkOrders/MarkOrderModal.vue'

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const TABS = [
  { key: 'unmarked', label: 'Unmarked Orders' },
  { key: 'marked',   label: 'Marked Orders' },
  { key: 'report',   label: 'Export Data' }
]

function toDateInput (date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const EMPTY_PAGE = { data: [], current_page: 1, per_page: 25, total: 0, last_page: 1 }

export default {
  name: 'MapBulkOrders',
  components: { UnmarkedOrdersTab, MarkedOrdersTab, ReportTab, MarkOrderModal },
  data () {
    return {
      TABS,
      loading: false,
      errorMessage: '',
      successMessage: '',
      lastUpdatedAt: null,
      activeTab: 'unmarked',
      startDateInput: '',
      endDateInput: '',
      appliedStartDate: '',
      appliedEndDate: '',
      perPage: 25,
      userName: '',
      users: [],
      unmarkedOrders: { ...EMPTY_PAGE },
      markedOrders: { ...EMPTY_PAGE },
      report: [],
      modal: {
        show: false,
        order: null,
        mode: 'mark',
        slNo: '',
        submitting: false
      }
    }
  },
  computed: {
    unmarkedPagination () {
      const o = this.unmarkedOrders || {}
      return {
        current_page: o.current_page || 1,
        per_page: o.per_page || this.perPage,
        total: o.total || 0,
        last_page: o.last_page || 1
      }
    },
    markedPagination () {
      const o = this.markedOrders || {}
      return {
        current_page: o.current_page || 1,
        per_page: o.per_page || this.perPage,
        total: o.total || 0,
        last_page: o.last_page || 1
      }
    },
    lastUpdatedLabel () {
      if (!this.lastUpdatedAt) return ''
      const hours = String(this.lastUpdatedAt.getHours()).padStart(2, '0')
      const minutes = String(this.lastUpdatedAt.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },
    appliedRangeLabel () {
      const start = this.formatDisplayDate(this.appliedStartDate)
      const end = this.formatDisplayDate(this.appliedEndDate)
      if (!start || !end) return ''
      return start === end ? start : `${start} → ${end}`
    },
    tabsWithCounts () {
      return [
        { key: 'unmarked', label: 'Unmarked Orders', count: this.unmarkedPagination.total },
        { key: 'marked',   label: 'Marked Orders',   count: this.markedPagination.total },
        { key: 'report',   label: 'Export Data',     count: null }
      ]
    },
    dateRange: {
      get () {
        return { fromDate: this.startDateInput, toDate: this.endDateInput }
      },
      set (next) {
        this.startDateInput = (next && next.fromDate) || ''
        this.endDateInput = (next && next.toDate) || ''
      }
    }
  },
  created () {
    const queryStart = (this.$route && this.$route.query && this.$route.query.start_date) || ''
    const queryEnd = (this.$route && this.$route.query && this.$route.query.end_date) || ''

    if (queryStart && queryEnd) {
      this.startDateInput = queryStart
      this.endDateInput = queryEnd
    } else {
      const today = toDateInput(new Date())
      this.startDateInput = today
      this.endDateInput = today
    }

    this.appliedStartDate = this.startDateInput
    this.appliedEndDate = this.endDateInput

    this.fetchData()
  },
  methods: {
    apiBase () {
      if (typeof window !== 'undefined') {
        const host = window.location.hostname
        if (host === 'localhost' || host === '127.0.0.1') {
          return ''
        }
      }
      return this.$apiAdress
    },
    authParams (extra = {}) {
      const token = localStorage.getItem('api_token') || ''
      return token ? { ...extra, token } : { ...extra }
    },
    onSearch () {
      this.appliedStartDate = this.startDateInput
      this.appliedEndDate = this.endDateInput
      this.unmarkedOrders.current_page = 1
      this.markedOrders.current_page = 1
      this.fetchData()
    },
    onUnmarkedPageChange (page) {
      this.unmarkedOrders.current_page = page
      this.fetchData()
    },
    onMarkedPageChange (page) {
      this.markedOrders.current_page = page
      this.fetchData()
    },
    async fetchData () {
      this.loading = true
      this.errorMessage = ''

      try {
        const response = await axios.get(`${this.apiBase()}/api/map-bulk-orders`, {
          params: this.authParams({
            start_date: this.appliedStartDate,
            end_date: this.appliedEndDate,
            unmarked_page: this.unmarkedOrders.current_page || 1,
            marked_page: this.markedOrders.current_page || 1,
            per_page: this.perPage
          })
        })

        const data = response.data || {}
        this.userName = data.user_name || this.userName
        this.unmarkedOrders = data.unmarked_orders || { ...EMPTY_PAGE }
        this.markedOrders = data.marked_orders || { ...EMPTY_PAGE }
        this.users = Array.isArray(data.users) ? data.users : []
        this.report = Array.isArray(data.report) ? data.report : []
        this.lastUpdatedAt = new Date()
      } catch (error) {
        console.log(error)
        this.errorMessage = (error && error.response && error.response.data && error.response.data.message) ||
          'Unable to load MAP bulk orders right now.'
      } finally {
        this.loading = false
      }
    },
    openMarkModal ({ order, slNo }) {
      this.modal = {
        show: true,
        order,
        mode: 'mark',
        slNo,
        submitting: false
      }
    },
    openChangeModal ({ order, slNo }) {
      this.modal = {
        show: true,
        order,
        mode: 'change',
        slNo,
        submitting: false
      }
    },
    closeModal () {
      this.modal = { show: false, order: null, mode: 'mark', slNo: '', submitting: false }
    },
    async submitMark (payload) {
      this.modal.submitting = true
      this.errorMessage = ''
      this.successMessage = ''

      try {
        const response = await axios.post(`${this.apiBase()}/api/map-bulk-orders/mark`, payload, {
          params: this.authParams()
        })

        if (response.data && response.data.success === false) {
          this.errorMessage = response.data.message || 'Failed to update care of.'
          return
        }

        this.closeModal()
        this.successMessage = 'Care of updated successfully.'
        setTimeout(() => { this.successMessage = '' }, 3000)
        await this.fetchData()
      } catch (error) {
        console.log(error)
        this.errorMessage = (error && error.response && error.response.data && error.response.data.message) ||
          'Failed to update care of.'
      } finally {
        this.modal.submitting = false
      }
    },
    formatDisplayDate (iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      const day = String(d.getDate()).padStart(2, '0')
      return `${day} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`
    }
  }
}
</script>

<style scoped>
.map-bulk-page {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.map-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.map-toolbar__title {
  display: flex;
  align-items: stretch;
  gap: 0.85rem;
}

.map-toolbar__accent {
  width: 4px;
  border-radius: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.map-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
}

.map-toolbar__title p {
  margin: 0.15rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.map-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.map-updated {
  font-size: 0.75rem;
  color: #6b7280;
}

.map-alert {
  margin-bottom: 1rem;
}

.map-filter-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  padding: 0.9rem 1.1rem;
  margin-bottom: 1rem;
}

.map-filter-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.map-filter-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.map-action-btn {
  height: 40px;
  min-width: 110px;
  padding: 0 1.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  border-radius: 999px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: none;
  color: #ffffff;
  transition: transform 0.15s ease, filter 0.15s ease;
}

.map-action-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.05);
}

.map-filter-footnote {
  margin-top: 0.65rem;
  padding-top: 0.65rem;
  border-top: 1px dashed #eef2f7;
  font-size: 0.75rem;
  color: #6b7280;
}

.report-tabs {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
  padding: 0.25rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  width: fit-content;
  max-width: 100%;
  overflow-x: auto;
  margin-bottom: 0.85rem;
}

.report-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.1rem;
  border: none;
  outline: none;
  background: transparent;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}

.report-tab:focus {
  outline: none;
  box-shadow: none;
}

.report-tab:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.25);
}

.report-tab:hover:not(.is-active) {
  background: #f1f5f9;
  color: #334155;
}

.report-tab.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 4px 10px rgba(10, 74, 58, 0.25);
}

.tab-count {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  background: rgba(14, 91, 70, 0.10);
  color: #0e5b46;
}

.report-tab.is-active .tab-count {
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

@media (max-width: 767px) {
  .map-filter-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .map-filter-actions {
    width: 100%;
  }

  .map-action-btn {
    flex: 1;
  }
}
</style>
